import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomSelect from "../../../components/ui/Select";
import CustomTextField from "../../../components/ui/Textfield";
import { RootState } from "../../../store/Index";
import { useIntroductionLetterTemplates } from "./hooks/use-introduction-letter";
import type {
  IntroductionLetterTemplate,
  TemplateFormData,
} from "./types/introduction-letter";
import { toast } from "react-toastify";

interface CreateIntroductionLetterModalProps {
  open: boolean;
  onClose: () => void;
  editTemplate?: IntroductionLetterTemplate | null;
  onSave: () => void;
}

const EXAMPLE_VALUES: Record<string, string> = {
  client_name: "Mr. John Smith",
  event_title: "Memorial Service",
  deceased_name: "Jane Doe",
};

const PLACEHOLDERS_BY_CATEGORY: Record<string, string[]> = {
  funeral: ["client_name", "deceased_name"],
  conference: ["client_name", "event_title"],
};

const TEMPLATE_CATEGORIES = [
  { value: "funeral", label: "Funeral" },
  // { value: "event", label: "Event" },
  { value: "conference", label: "Conference" },
] as const;

const CreateIntroductionLetterModal: React.FC<
  CreateIntroductionLetterModalProps
> = ({ open, onClose, editTemplate, onSave }) => {
  const theme = useSelector((state: RootState) => state.theme);
  const { saveTemplate, updateTemplate, fetchTemplates, loading, templates } =
    useIntroductionLetterTemplates();

  const [templateData, setTemplateData] = useState<TemplateFormData>({
    name: "",
    category: "Funeral",
    body_template: "",
    is_default_template: false,
  });

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (editTemplate) {
      setTemplateData({
        name: editTemplate.name,
        category: editTemplate.category,
        body_template: editTemplate.body_template,
        is_default_template: editTemplate.is_default_template ?? false,
      });
    } else {
      setTemplateData({
        name: "",
        category: "funeral",
        body_template: "",
        is_default_template: false,
      });
    }
  }, [editTemplate, open]);

  const handleChange = (
    field: keyof TemplateFormData,
    value: string | boolean,
  ) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  const validPlaceholders =
    PLACEHOLDERS_BY_CATEGORY[templateData.category] ?? [];

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById(
      "highlighted-textarea",
    ) as HTMLTextAreaElement | null;

    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      templateData.body_template.slice(0, start) +
      placeholder +
      templateData.body_template.slice(end);

    setTemplateData((prev) => ({ ...prev, body_template: newValue }));

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + placeholder.length;
      textarea.focus();
    }, 0);
  };

  const highlightVariables = (text: string) => {
    let result = text;

    validPlaceholders.forEach((key) => {
      const placeholder = `{${key}}`;

      result = result.replaceAll(
        placeholder,
        `<span style="color: red; font-weight: bold">${EXAMPLE_VALUES[key]}</span>`,
      );
    });

    return result;
  };

  const handleSave = async () => {
    try {
      if (templateData.is_default_template) {
        const currentDefault = templates.find(
          (t) =>
            t.category === templateData.category &&
            t.is_default_template === true &&
            t.id !== editTemplate?.id,
        );

        if (currentDefault) {
          await updateTemplate(currentDefault.id, {
            is_default_template: false,
          });
        }
      }

      if (editTemplate?.id) {
        await updateTemplate(editTemplate.id, templateData);
        toast.success("Template updated successfully!");
      } else {
        await saveTemplate(templateData);
        toast.success("Template created successfully!");
      }

      onSave();
      onClose();
    } catch {
      toast.error(
        editTemplate
          ? "Failed to update template. Please try again."
          : "Failed to create template. Please try again.",
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: theme.primary_colors[100] }}>
        {editTemplate
          ? "Edit Introduction Letter Template"
          : "Create Introduction Letter Template"}
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.primary_colors[900] }}>
        <Box display="flex" gap={3} mt={2}>
          <Box flex={2} display="flex" flexDirection="column" gap={3}>
            <CustomTextField
              label="Template Name"
              fullWidth
              value={templateData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              variant="outlined"
            />

            <CustomSelect
              label="Category"
              value={templateData.category}
              onChange={(e) =>
                handleChange("category", e.target.value as string)
              }
            >
              {TEMPLATE_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </CustomSelect>

            <CustomTextField
              id="highlighted-textarea"
              label="Introduction Letter Body"
              multiline
              minRows={10}
              value={templateData.body_template}
              onChange={(e) => handleChange("body_template", e.target.value)}
              variant="outlined"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={templateData.is_default_template ?? false}
                  onChange={(e) =>
                    handleChange("is_default_template", e.target.checked)
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: theme.primary_colors[400],
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: theme.primary_colors[400],
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  {templateData.is_default_template
                    ? "Default template"
                    : "Set as default template"}
                </Typography>
              }
            />

            <Typography
              variant="subtitle2"
              sx={{ color: theme.primary_colors[300] }}
            >
              Available variables for your introduction letter:
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1}>
              {validPlaceholders.map((p) => (
                <Chip
                  key={p}
                  label={`{${p}}`}
                  onClick={() => insertPlaceholder(`{${p}}`)}
                  sx={{
                    backgroundColor: theme.primary_colors[900],
                    color: theme.primary_colors[400],
                    border: "1px solid #614338",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: theme.primary_colors[500],
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            flex={1}
            p={2}
            border={`1px solid ${theme.primary_colors[400]}`}
            borderRadius={2}
            bgcolor={theme.primary_colors[900]}
            height="fit-content"
            maxHeight="500px"
            overflow="auto"
            boxShadow="inset 0 0 4px rgba(0,0,0,0.2)"
          >
            <Typography
              fontWeight="bold"
              mb={2}
              sx={{ color: theme.primary_colors[100] }}
            >
              Live Preview
            </Typography>

            <Typography
              component="div"
              whiteSpace="pre-wrap"
              sx={{ lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{
                __html:
                  highlightVariables(templateData.body_template) ||
                  "<em>Start typing to see preview...</em>",
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: theme.primary_colors[900] }}>
        <Button onClick={onClose} sx={{ color: theme.primary_colors[300] }}>
          Cancel
        </Button>
        <Button
          disabled={
            loading || !templateData.name || !templateData.body_template
          }
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: theme.primary_colors[400],
            color: theme.primary_colors[900],
            "&:hover": {
              backgroundColor: theme.primary_colors[500],
            },
          }}
        >
          {editTemplate ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIntroductionLetterModal;
